/**
 * Print Transport Layer
 *
 * Handles sending ZPL data to printers via:
 * - WebUSB (direct USB connection)
 * - Web Bluetooth
 * - Network (TCP/IP via proxy or raw socket)
 *
 * Each transport implements a common interface for discovery and printing.
 */
import type { DiscoveredPrinter, PrintTransportType } from '../../types/printing';

// Zebra USB vendor ID
const ZEBRA_VENDOR_ID = 0x0a5f;

// Common Zebra Bluetooth service UUID
const ZEBRA_BT_SERVICE_UUID = '38eb4a80-c570-11e3-9507-0002a5d5c51b';
const ZEBRA_BT_WRITE_CHAR_UUID = '38eb4a82-c570-11e3-9507-0002a5d5c51b';

// ---- USB Transport ----

let cachedUSBDevice: USBDevice | null = null;

async function getUSBDevice(): Promise<USBDevice> {
  if (cachedUSBDevice) {
    try {
      // Verify the device is still connected
      const devices = await navigator.usb.getDevices();
      if (devices.some(d => d === cachedUSBDevice)) {
        return cachedUSBDevice;
      }
    } catch {
      cachedUSBDevice = null;
    }
  }

  // Check for previously approved devices
  const devices = await navigator.usb.getDevices();
  const zebraDevice = devices.find(d => d.vendorId === ZEBRA_VENDOR_ID);
  if (zebraDevice) {
    cachedUSBDevice = zebraDevice;
    return cachedUSBDevice;
  }

  // Request new device
  cachedUSBDevice = await navigator.usb.requestDevice({
    filters: [{ vendorId: ZEBRA_VENDOR_ID }],
  });
  return cachedUSBDevice;
}

export async function printViaUSB(zplData: string): Promise<void> {
  const device = await getUSBDevice();

  await device.open();
  if (!device.configuration) {
    await device.selectConfiguration(1);
  }
  await device.claimInterface(0);

  const outEndpoint = device.configuration!.interfaces[0].alternates[0].endpoints.find(
    ep => ep.direction === 'out'
  );

  if (!outEndpoint) {
    throw new Error('No output endpoint found on USB device');
  }

  const encoder = new TextEncoder();
  const data = encoder.encode(zplData);

  // Send in chunks of 4096 bytes for reliability
  const chunkSize = 4096;
  for (let offset = 0; offset < data.byteLength; offset += chunkSize) {
    const chunk = data.slice(offset, offset + chunkSize);
    await device.transferOut(outEndpoint.endpointNumber, chunk);
  }
}

export async function discoverUSBPrinters(): Promise<DiscoveredPrinter[]> {
  if (!navigator.usb) return [];

  try {
    const devices = await navigator.usb.getDevices();
    return devices
      .filter(d => d.vendorId === ZEBRA_VENDOR_ID)
      .map(d => ({
        id: `usb-${d.serialNumber || d.productId}`,
        name: d.productName || `Zebra USB (${d.productId.toString(16)})`,
        transport: 'usb' as PrintTransportType,
        address: d.serialNumber || undefined,
        device: d,
        status: 'available' as const,
      }));
  } catch {
    return [];
  }
}

// ---- Bluetooth Transport ----

export async function printViaBluetooth(zplData: string): Promise<void> {
  const device = await navigator.bluetooth.requestDevice({
    filters: [{ services: [ZEBRA_BT_SERVICE_UUID] }],
    optionalServices: [ZEBRA_BT_SERVICE_UUID],
  });

  if (!device.gatt) {
    throw new Error('Bluetooth GATT not available');
  }

  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(ZEBRA_BT_SERVICE_UUID);
  const characteristic = await service.getCharacteristic(ZEBRA_BT_WRITE_CHAR_UUID);

  const encoder = new TextEncoder();
  const data = encoder.encode(zplData);

  // BLE has smaller MTU, send in 512-byte chunks
  const chunkSize = 512;
  for (let offset = 0; offset < data.byteLength; offset += chunkSize) {
    const chunk = data.slice(offset, offset + chunkSize);
    await characteristic.writeValue(chunk);
  }

  device.gatt.disconnect();
}

export async function discoverBluetoothPrinters(): Promise<DiscoveredPrinter[]> {
  if (!navigator.bluetooth) return [];

  // Bluetooth scanning requires user gesture, so we return empty for passive discovery
  // Active pairing happens through printViaBluetooth which triggers requestDevice
  return [];
}

// ---- Network Transport ----

export async function printViaNetwork(
  zplData: string,
  address: string,
  port = 9100
): Promise<void> {
  // Network printing requires a proxy server or raw TCP socket.
  // We'll use a backend endpoint on the worker to relay data.
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';

  const response = await fetch(`${apiUrl}/print-jobs/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      transport: 'network',
      address,
      port,
      zplData,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Network print failed' }));
    throw new Error(err.error || 'Network print failed');
  }
}

// ---- Clipboard Transport (fallback) ----

export async function copyToClipboard(zplData: string): Promise<void> {
  await navigator.clipboard.writeText(zplData);
}

// ---- Unified Transport Interface ----

export async function discoverPrinters(): Promise<DiscoveredPrinter[]> {
  const results: DiscoveredPrinter[] = [];

  const [usbPrinters, btPrinters] = await Promise.allSettled([
    discoverUSBPrinters(),
    discoverBluetoothPrinters(),
  ]);

  if (usbPrinters.status === 'fulfilled') {
    results.push(...usbPrinters.value);
  }
  if (btPrinters.status === 'fulfilled') {
    results.push(...btPrinters.value);
  }

  return results;
}

export async function sendToPrinter(
  zplData: string,
  transport: PrintTransportType,
  options?: { address?: string; port?: number }
): Promise<void> {
  switch (transport) {
    case 'usb':
      return printViaUSB(zplData);
    case 'bluetooth':
      return printViaBluetooth(zplData);
    case 'network':
      if (!options?.address) throw new Error('Network address required');
      return printViaNetwork(zplData, options.address, options.port);
    case 'clipboard':
      return copyToClipboard(zplData);
    default:
      throw new Error(`Unknown transport: ${transport}`);
  }
}

// ---- Feature Detection ----

export function getAvailableTransports(): PrintTransportType[] {
  const transports: PrintTransportType[] = [];

  // Prefer USB (WebUSB) as default when available
  if (typeof navigator !== 'undefined') {
    if ('usb' in navigator) transports.push('usb');
    if ('bluetooth' in navigator) transports.push('bluetooth');
  }

  // Network is always available via proxy
  transports.push('network');

  // Clipboard always available as fallback
  transports.push('clipboard');

  return transports;
}
